// Fiscal-year helpers used by the chat assistant's natural-language query
// parser. These mirror the math in src/Helpers/ContractDateHelper.tsx so chat
// results are bucketed exactly the same way the rest of the app buckets them.
//
// Convention (matches ContractDateHelper.getStartContractDate /
// getFiscalYear):
//   start month = fiscalMonthOffset + 1
//   end month   = fiscalMonthOffset (of the following year)
// So for fiscalMonthOffset = 0, fiscal year N runs Jan N -> Dec N.
// For fiscalMonthOffset = 6, fiscal year N runs Jul N -> Jun (N+1).
// For fiscalMonthOffset = 9, fiscal year N runs Oct N -> Sep (N+1).
import { ContractDate } from '../../../../Generated/Commodity-Management-Api';

export interface FiscalYearRange {
    fiscalYear: number;
    start: ContractDate;   // inclusive first budget/contract month
    end: ContractDate;     // inclusive last budget/contract month
}

// Expand a bare fiscal year (e.g. user said "2026") into the inclusive
// start/end ContractDate range. The end is computed by adding 11 months
// to the start so we cover exactly 12 budget months, matching how
// BasicQueryForm.tsx builds its position queries.
export function expandFiscalYearToBudgetMonthRange(
    fiscalYear: number,
    fiscalMonthOffset: number | undefined | null
): FiscalYearRange {
    const offset = Math.max(0, Math.min(11, Math.floor(fiscalMonthOffset ?? 0)));

    // Start month is one past the offset (calendar Jan when offset is 0).
    const startMonthRaw = offset + 1;
    const start: ContractDate = {
        year: fiscalYear,
        month: startMonthRaw,
    };

    // Adding 11 months gives the last covered month. Handles year rollover.
    const endMonthZeroBased = (startMonthRaw - 1) + 11; // 0..22
    const end: ContractDate = {
        year: fiscalYear + Math.floor(endMonthZeroBased / 12),
        month: (endMonthZeroBased % 12) + 1,
    };

    return { fiscalYear, start, end };
}

// Pull a 4-digit fiscal year out of a chat message. Returns the first
// reasonable year found (2000-2099) or undefined when none is present.
// Intentionally does not handle ranges like "2025-2027" - the parser
// composes those by calling this helper per year.
export function parseFiscalYearFromMessage(message: string): number | undefined {
    if (!message) return undefined;
    const match = message.match(/\b(20\d{2})\b/);
    if (!match) return undefined;
    const year = Number.parseInt(match[1], 10);
    if (Number.isNaN(year) || year < 2000 || year > 2099) return undefined;
    return year;
}

// Pull all 4-digit fiscal years out of a chat message in the order they
// appear. Used so "show me 2025 and 2026 corn positions" can expand into
// two fiscal-year ranges.
export function parseAllFiscalYearsFromMessage(message: string): number[] {
    if (!message) return [];
    const years: number[] = [];
    const seen = new Set<number>();
    const regex = /\b(20\d{2})\b/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(message)) !== null) {
        const year = Number.parseInt(match[1], 10);
        if (!Number.isNaN(year) && year >= 2000 && year <= 2099 && !seen.has(year)) {
            seen.add(year);
            years.push(year);
        }
    }
    return years;
}

// Render a fiscal year range as a short, user-facing label so the chat
// can echo back the exact months it filtered on. e.g.
//   FY 2026 (Jul 2026 - Jun 2027)
// For a calendar-aligned customer (offset 0) the label collapses to
// just the year so it doesn't read awkwardly.
export function describeFiscalYearRange(range: FiscalYearRange): string {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const startLabel = `${monthNames[(range.start.month ?? 1) - 1]} ${range.start.year}`;
    const endLabel = `${monthNames[(range.end.month ?? 1) - 1]} ${range.end.year}`;
    if (range.start.year === range.fiscalYear && range.end.year === range.fiscalYear && range.start.month === 1 && range.end.month === 12) {
        return `${range.fiscalYear}`;
    }
    return `FY ${range.fiscalYear} (${startLabel} - ${endLabel})`;
}
